import Pagination from "@src/common/Pagination";
import FullPageLoader from "@src/common/FullPageLoader";
import { DEFAULT_PAGE_NO, DEFAULT_PAGINATION_COUNT, WHATSAPP_WEB_URL } from "@src/config/constant";
import { generateTravelQrCode } from "@src/store/slices/travel";
import { saveAs } from "file-saver";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    TRAVEL_LIST_ACTIONS,
    TRAVEL_LIST_COLUMNS,
    TravelListRow,
    TravelListStatus,
} from "./travelListData";

type TravelListTableProps = {
    travelList: TravelListRow[];
    paginationData: any;
    selectedFilters: any;
    getTravelListing: (params: any) => void;
    activeTab:string
};

const getTripDetails = (lead: TravelListRow, activeStatus: TravelListStatus) => {
    const visibleCount = activeStatus === "quotation_shared" ? 3 : 2;
    const visibleDestinations = lead.destinations.slice(0, visibleCount).join(", ");
    const hiddenCount = lead.destinations.length - visibleCount;

    return (
        <span title={`${lead.destinations.join(", ")} - ${lead.tripType}`}>
            {visibleDestinations}
            {hiddenCount > 0 && <button className="travel-more-destinations" type="button">+{hiddenCount}</button>}
            <span className="travel-trip-separator">•</span>
            {lead.tripType}
        </span>
    );
};

const TravelListTable = (props: TravelListTableProps) => {
    const { travelList, paginationData, selectedFilters,activeTab } = props;
    const dispatch: any = useDispatch();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const activeStatus: TravelListStatus = selectedFilters.status_type || "quotation_shared";
    const columns = TRAVEL_LIST_COLUMNS[activeStatus];
    const actions = TRAVEL_LIST_ACTIONS[activeStatus] || [];
    const page = DEFAULT_PAGE_NO;

    const resolveUrl = (url: string) => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) return "";

        if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl;
        if (trimmedUrl.startsWith("//")) return `${window.location.protocol}${trimmedUrl}`;
        if (trimmedUrl.startsWith("/")) return `${window.location.origin}${trimmedUrl}`;

        return `${window.location.origin}/${trimmedUrl}`;
    };

    const openInNewTabAndDownload = (url: string) => {
        const resolvedUrl = resolveUrl(url);
        if (!resolvedUrl) return;

        window.open(resolvedUrl, "_blank", "noopener,noreferrer");

        // const link = document.createElement("a");
        // link.href = resolvedUrl;
        // link.download = "";
        // link.rel = "noopener noreferrer";
        // document.body.appendChild(link);
        // link.click();
        // link.remove();
    };

    const handleActionClick = (actionKey: string, lead: TravelListRow) => {
        if (actionKey !== "download_policy") return;

        const downloadUrl = (lead?.download_policy || "").trim();

        if (!downloadUrl) {
            toast.error("Policy download link not available.");
            return;
        }

        openInNewTabAndDownload(downloadUrl);
    };

    const getRowActions = (lead: TravelListRow) => {
        if (activeStatus !== "submitted" || lead.status !== "Paid") {
            return actions;
        }

        return actions.filter((action) => action.key !== "generate_qr");
    };

    const handleRequestData = (params: any) => {
        props.getTravelListing({
            ...selectedFilters,
            page_number: params.page_no,
        });
    };

    const handleSharedOnSort = () => {
        props.getTravelListing({
            ...selectedFilters,
            page_number: 1,
            shared_on_sort: selectedFilters?.shared_on_sort === "ASC" ? "DESC" : "ASC",
        });
    };

    const handleEdit = (lead: TravelListRow) => {
        navigate(`/create-travel-insurance?step=checkout&lead_id=${lead.id}`);
    };

    const handleAction = (actionKey: string, lead: TravelListRow) => {
        handleActionClick(actionKey,lead)
        if (actionKey === "show_quotation" && lead.quotationUrl) {
            window.open(lead.quotationUrl, "_blank", "noopener,noreferrer");
        }

        if (actionKey === "share_quotation" && lead.quotationUrl) {
            const shareText = `Travel quotation: ${lead.quotationUrl}`;
            window.open(`${WHATSAPP_WEB_URL}${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
        }
    }
    
    const handleGenerateQrCode = async (lead: TravelListRow) => {
        setLoading(true);
        try {
            const response: any = await dispatch(generateTravelQrCode({ lead_id: lead.id }));
            const qrCode = response?.data?.qr_code;

            if (qrCode) saveAs(qrCode, `qr_code_${response?.data?.lead_id || lead.id}.png`);
        } finally {
            setLoading(false);
        }
    };

    const renderCell = (lead: TravelListRow, columnKey: string) => {
        switch (columnKey) {
            case "customerName":
                return <h5 title={lead.customerName}>{lead.customerName}</h5>;
            case "tripDetails":
                return getTripDetails(lead, activeStatus);
            case "planName":
                return <span title={lead.planName}>{lead.planName}</span>;
            case "sharedOn":
                return <span>{lead.sharedOn}</span>;
            case "submittedOn":
                return <span>{lead.submittedOn}</span>;
            case "issuedOn":
                return <span>{lead.issuedOn}</span>;
            case "policyNo":
                return <span title={lead.policyNo}>{lead.policyNo}</span>;
            case "status":
                return lead.status ? <span className={`travel-status-badge ${activeStatus}`}>{lead.status}</span> : null;
            case "action":
                return (
                    <div className="travel-list-actions">
                        {
                            (activeTab === 'quotation_shared') && (
                                <button className="travel-list-action-btn edit" type="button" title="Edit" aria-label="Edit" onClick={() => handleEdit(lead)}>
                                    <i className="ic-edit"></i>
                                </button>
                            )
                        }
                        {getRowActions(lead).map((action) => (
                            <button
                                className={`travel-list-action-btn ${action.key}`}
                                key={action.key}
                                type="button"
                                onClick={action.key === "generate_qr" ? () => handleGenerateQrCode(lead) : () => handleAction(action.key, lead)}
                            >
                                <i className={action.icon}></i>
                                {action.label}
                            </button>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <FullPageLoader show={loading} />
            <div className={`lead-list-data-table data-table managepanel-tab wap-table travel-list-data-table ${activeStatus}`}>
                <table>
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th key={column.key}>
                                    {column.label}
                                    {column.sortable && column.key === "sharedOn" && (
                                        <i
                                            className={`${selectedFilters?.shared_on_sort === "ASC" ? "ic-keyboard_arrow_down" : "ic-keyboard_arrow_up"} travel-sort-icon`}
                                            onClick={handleSharedOnSort}
                                        ></i>
                                    )}
                                    {column.sortable && column.key !== "sharedOn" && <i className="ic-keyboard_arrow_up travel-sort-icon"></i>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {travelList.length ? (
                            travelList.map((lead) => (
                                <tr key={lead.id}>
                                    {columns.map((column) => (
                                        <td key={column.key} data-label={column.label} className={`travel-list-cell-${column.key}`}>
                                            <div className="table-txt-data cursor-point">
                                                {renderCell(lead, column.key)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="travel-empty-state">No data found</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {(paginationData && paginationData.total > DEFAULT_PAGINATION_COUNT) && (
                    <Pagination
                        paginateData={{
                            page,
                            loading: false,
                            totalLengthWithoutPagination: paginationData.total,
                            paginationData,
                            filteredData: selectedFilters,
                        }}
                        sendRequestDataToParent={handleRequestData}
                    />
                )}
            </div>
        </>
    );
};

export default TravelListTable;
